import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, IconButton, Typography } from '@one-thd/sui-atomic-components';
import { Close, ErrorFilled, PDF, Upload } from '@one-thd/sui-icons';
import {
  ACCEPTED_FILE_TYPES,
  MAX_FILE_SIZE_BYTES,
  MAX_FILE_SIZE_MB,
  UPLOAD_STATES,
  readFileAsBase64
} from './aiListBuilderHelpers';

/**
 * Optional reference image upload for the AI List Builder.
 *
 * Click-to-browse only (no drag-and-drop, no camera capture). On a valid
 * selection the file is converted to raw base64 and surfaced to the parent
 * via onImageChange; on remove or an invalid selection onImageChange(null)
 * is called. All object URLs are revoked on change/remove/unmount.
 *
 * The file is never stored client-side. It is read to base64 in the browser, held
 * in React state for the life of the interaction, and sent inline with the generate
 * request — no local/session storage, no bucket, no separate upload endpoint. What
 * the list agent retains server-side is its concern, not this component's.
 *
 * The upload control is a full-width button that stays visible even after a
 * file is attached (clicking it again re-opens the picker to replace the
 * file); the attached file's preview/name/status render above it.
 */
export const ReferenceImageUpload = ({ onImageChange, onUploadingChange, disabled }) => {
  const fileInputRef = useRef(null);
  // Bumped on every new selection or removal; lets an in-flight readFileAsBase64
  // from a superseded selection detect it's stale and skip applying its result.
  const selectionIdRef = useRef(0);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [fileName, setFileName] = useState('');
  const [fileType, setFileType] = useState('');
  const [uploadState, setUploadState] = useState(UPLOAD_STATES.IDLE);
  const [uploadError, setUploadError] = useState('');

  // Revoke the object URL when it changes or the component unmounts.
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  // Surface read-in-progress so the parent can block Generate: until the read
  // resolves, onImageChange has not fired and the request would go text-only.
  useEffect(() => {
    onUploadingChange(uploadState === UPLOAD_STATES.UPLOADING);
  }, [uploadState, onUploadingChange]);

  const resetSelection = useCallback(() => {
    setPreviewUrl(null);
    setFileName('');
    setFileType('');
    setUploadState(UPLOAD_STATES.IDLE);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const handleFileSelect = useCallback((event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Invalidate any read still in flight from a previous selection.
    selectionIdRef.current += 1;
    const selectionId = selectionIdRef.current;

    // Validate file size
    if (file.size > MAX_FILE_SIZE_BYTES) {
      resetSelection();
      setUploadState(UPLOAD_STATES.ERROR);
      setUploadError(`File is too large. Maximum size is ${MAX_FILE_SIZE_MB}MB.`);
      onImageChange(null);
      return;
    }

    // Validate file type
    const allowedTypes = ACCEPTED_FILE_TYPES.split(',');
    if (!allowedTypes.includes(file.type)) {
      resetSelection();
      setUploadState(UPLOAD_STATES.ERROR);
      setUploadError('Invalid file type. Please upload a JPEG, PNG, WebP, or PDF file.');
      onImageChange(null);
      return;
    }

    setUploadError('');
    setFileName(file.name);
    setFileType(file.type);
    setPreviewUrl(URL.createObjectURL(file));
    setUploadState(UPLOAD_STATES.UPLOADING);

    readFileAsBase64(file)
      .then(({ base64 }) => {
        if (selectionIdRef.current !== selectionId) return; // superseded, ignore
        setUploadState(UPLOAD_STATES.COMPLETE);
        onImageChange({ base64, mimeType: file.type, fileName: file.name });
      })
      .catch((err) => {
        if (selectionIdRef.current !== selectionId) return; // superseded, ignore
        setUploadState(UPLOAD_STATES.ERROR);
        setUploadError(err.message || 'Failed to read file. Please try again.');
        onImageChange(null);
      });
  }, [onImageChange, resetSelection]);

  const handleRemove = useCallback(() => {
    selectionIdRef.current += 1; // invalidate any read still in flight
    resetSelection();
    setUploadError('');
    onImageChange(null);
  }, [onImageChange, resetSelection]);

  const handleChoose = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, []);

  return (
    <div className="sui-flex sui-flex-col sui-gap-2">
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_FILE_TYPES}
        onChange={handleFileSelect}
        disabled={disabled}
        className="sui-hidden"
        data-testid="reference-image-input"
      />

      {previewUrl && (
        <div
          className="sui-flex sui-items-center sui-gap-3 sui-px-2 sui-h-11 sui-border-1
            sui-border-solid sui-border-primary sui-rounded-lg"
        >
          <div className="sui-relative sui-w-8 sui-h-8 sui-rounded sui-overflow-hidden sui-flex-shrink-0">
            {fileType === 'application/pdf' ? (
              <div
                className="sui-w-full sui-h-full sui-flex sui-items-center sui-justify-center"
                data-testid="reference-image-pdf-preview"
              >
                <PDF size="regular" color="medium" />
              </div>
            ) : (
              <img
                width={32}
                height={32}
                src={previewUrl}
                alt={`Reference preview of ${fileName}`}
                className="sui-w-full sui-h-full sui-object-cover"
                data-testid="reference-image-preview"
              />
            )}
          </div>
          <div className="sui-flex sui-flex-col sui-flex-1 sui-min-w-0">
            <Typography variant="body-sm" weight="bold" className="sui-truncate">
              {fileName}
            </Typography>
            {uploadState === UPLOAD_STATES.UPLOADING && (
              <Typography variant="body-sm" color="subtle">Loading...</Typography>
            )}
            {uploadState === UPLOAD_STATES.COMPLETE && (
              <Typography variant="body-sm" color="success">Loaded &amp; ready</Typography>
            )}
          </div>
          <IconButton
            icon={Close}
            aria-label="Remove photo"
            onClick={handleRemove}
            disabled={disabled}
            data-testid="reference-image-remove"
          />
        </div>
      )}

      <Button
        variant="tertiary"
        startIcon={Upload}
        onClick={handleChoose}
        disabled={disabled}
        fullWidth
        data-testid="reference-image-choose"
      >
        Upload File
      </Button>

      {uploadError && (
        <span className="sui-flex sui-items-top sui-gap-1 sui-text-danger">
          <ErrorFilled color="danger" />
          <Typography color="danger" variant="body-sm" data-testid="reference-image-error">
            {uploadError}
          </Typography>
        </span>
      )}
    </div>
  );
};

ReferenceImageUpload.displayName = 'ReferenceImageUpload';

ReferenceImageUpload.propTypes = {
  onImageChange: PropTypes.func.isRequired,
  onUploadingChange: PropTypes.func,
  disabled: PropTypes.bool,
};

ReferenceImageUpload.defaultProps = {
  onUploadingChange: () => {},
  disabled: false,
};
